import React, { useEffect, useState, useCallback, useMemo } from "react";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Card from "@mui/material/Card";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import HOST_URL from "../../../utils/Url";
import { toast } from "react-toastify";
import { usePermissionData } from "../../../hooks/usePermissionData";

interface MenuData {
  menuId: number;
  parentName: string;
  menuName: string;
  pageUrl: string;
  displayNo: number;
  icon: string;
  id: number;
  serialNo: number;
}

export default function MenuMaster() {
  const [menus, setMenus] = useState<MenuData[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string>("");

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { permissionData, loading: permissionLoading } = usePermissionData();

  // Delete handlers
  const handleDeleteAccept = useCallback(async (id: string) => {
    try {
      const collectData = {
        menuId: id,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `Menu/DeleteMenuMaster`,
        collectData
      );

      if (response.data.isSuccess) {
        toast.success(response.data.mesg);
        getList();
      } else {
        toast.error(response.data.mesg);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete menu");
    } finally {
      setDeleteId("");
    }
  }, []);

  const handleDeleteReject = useCallback(() => {
    toast.warn("Deletion cancelled", { autoClose: 3000 });
    setDeleteId("");
  }, []);

  // Memoized fetch function
  const getList = useCallback(async () => {
    try {
      setIsLoading(true);
      const collectData = {
        menuId: -1
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `Menu/GetMenuMaster`,
        collectData
      );

      if (response.data.data) {
        const menusWithIds = response.data.data.map((menu: any, index: number) => ({
          ...menu,
          serialNo: index + 1,
          id: menu.menuId,
        }));
        setMenus(menusWithIds);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load menus");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    getList();
  }, [getList]);

  // Setup columns based on permission data
  useEffect(() => {
    if (permissionLoading || menus.length === 0) return;

    const baseColumns: GridColDef[] = [
      {
        field: "serialNo",
        headerName: t("text.SrNo"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 60
      },
      {
        field: "parentName",
        headerName: t("text.ParentName"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 120
      },
      {
        field: "menuName",
        headerName: t("text.MenuName"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 150
      },
      {
        field: "pageUrl",
        headerName: t("text.PageURL"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 200
      },
      {
        field: "displayNo",
        headerName: "Display No",
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 100
      },
      {
        field: "icon",
        headerName: t("text.Icon"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 120
      },
    ];

    // Add actions column only if user has edit or delete permissions
    if (permissionData?.isEdit || permissionData?.isDel) {
      const actionsColumn: GridColDef = {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 150,
        renderCell: (params) => (
          <Stack
            spacing={1}
            direction="row"
            sx={{ alignItems: "center", marginTop: "5px" }}
          >
            {permissionData?.isEdit && (
              <EditIcon
                style={{
                  fontSize: "20px",
                  color: "blue",
                  cursor: "pointer",
                }}
                className="cursor-pointer"
                onClick={() => routeChangeEdit(params.row)}
              />
            )}
            {permissionData?.isDel && (
              <DeleteIcon
                style={{
                  fontSize: "20px",
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setDeleteId(params.row.id.toString());
                  confirmDialog({
                    message: "Do you want to delete this record?",
                    header: "Delete Confirmation",
                    icon: "pi pi-info-circle",
                    acceptClassName: "p-button-danger",
                    accept: () => handleDeleteAccept(params.row.id.toString()),
                    reject: handleDeleteReject,
                  });
                }}
              />
            )}
          </Stack>
        ),
      };

      setColumns([actionsColumn, ...baseColumns]);
    } else {
      setColumns(baseColumns);
    }
  }, [permissionData, permissionLoading, menus.length, t, handleDeleteAccept, handleDeleteReject]);

  // Navigation handlers
  const routeChangeEdit = (row: MenuData) => {
    navigate(`/master/MenuMasterEdit`, {
      state: row,
    });
  };

  const routeChangeAdd = () => {
    navigate(`/master/MenuMasterAdd`);
  };

  // Memoized adjusted columns
  const adjustedColumns = useMemo(() =>
    columns.map(column => ({ ...column })),
    [columns]
  );

  // Loading state handling
  if (permissionLoading) {
    return (
      <Grid item lg={6} sm={6} xs={12}>
        <Card
          style={{
            width: "100%",
            height: "50%",
            backgroundColor: "#E9FDEE",
            border: ".5px solid green ",
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </Card>
      </Grid>
    );
  }

  return (
    <div>
      <Grid item lg={6} sm={6} xs={12}>
        <Card
          style={{
            width: "100%",
            height: "50%",
            backgroundColor: "#E9FDEE",
            border: ".5px solid green ",
          }}
        >
          <Paper
            sx={{
              width: "100%",
              overflow: "hidden",
              "& .MuiDataGrid-colCell": {
                backgroundColor: `var(--grid-headerBackground)`,
                color: `var(--grid-headerColor)`,
                fontSize: 17,
                fontWeight: 900,
              },
            }}
            style={{ padding: "10px" }}
          >
            <ConfirmDialog />
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              sx={{ padding: "20px" }}
              align="left"
            >
              {t("text.MenuMaster")}
            </Typography>
            <Divider />

            {/* Search and ADD button Start */}
            <Box height={10} />

            {/* Action Buttons - Only show if user has add permission */}
            {permissionData?.isAdd && (
              <Stack direction="row" spacing={2} classes="my-2 mb-2">
                <Button
                  onClick={routeChangeAdd}
                  variant="contained"
                  endIcon={<AddCircleIcon />}
                  style={{ backgroundColor: `var(--header-background)` }}
                >
                  {t("text.add")}
                </Button>
              </Stack>
            )}

            {/* Data Grid Section */}
            {isLoading ? (
              <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                rows={menus}
                columns={adjustedColumns}
                autoHeight
                slots={{
                  toolbar: GridToolbar,
                }}
                rowSpacingType="border"
                pagination={true}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 5 } },
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                  },
                }}
                disableRowSelectionOnClick
              />
            )}
          </Paper>
        </Card>
      </Grid>
    </div>
  );
}














// import * as React from "react";
// import Paper from "@mui/material/Paper";
// import Table from "@mui/material/Table";
// import TableBody from "@mui/material/TableBody";
// import TableCell from "@mui/material/TableCell";
// import TableContainer from "@mui/material/TableContainer";
// import TableHead from "@mui/material/TableHead";
// import TablePagination from "@mui/material/TablePagination";
// import TableRow from "@mui/material/TableRow";
// import { useEffect, useState } from "react";
// import {
//   Box,
//   Button,
//   Divider,
//   Stack,
//   TextField,
//   Typography,
// } from "@mui/material";
// import AddCircleIcon from "@mui/icons-material/AddCircle";
// import SearchIcon from "@mui/icons-material/Search";
// import Swal from "sweetalert2";
// import EditIcon from "@mui/icons-material/Edit";
// import DeleteIcon from "@mui/icons-material/Delete";
// import Card from "@mui/material/Card";
// import Grid from "@mui/material/Grid";
// import PrintIcon from "@mui/icons-material/Print";
// import { useNavigate, useLocation } from "react-router-dom";
// import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
// import axios from "axios";
// import api from "../../../utils/Url";
// import CircularProgress from "@mui/material/CircularProgress";
// import { useTranslation } from "react-i18next";
// import {
//   DataGrid,
//   GridColDef,
//   GridToolbar,
// } from "@mui/x-data-grid";
// import HOST_URL from "../../../utils/Url";


// import { toast, ToastContainer } from "react-toastify";

// function createData(
//   srno: number,
//   id: string,
//   menuName: string,
//   parentId: number,
//   parentName: string,
//   pageUrl: string,
//   icon: string,
//   displayNo: number,
//   // isMenu: boolean,
//   isAdd: boolean,
//   isEdit: boolean,
//   isDel: boolean,
//   isView: boolean,
//   isPrint: boolean,
//   isExport: boolean,
//   isRelease: boolean,
//   isPost: boolean,

// ): any {
//   return {
//     srno, id, menuName, pageUrl, icon,
//     displayNo,
//     // isMenu,
//     isAdd, isEdit, isDel, isView, isPrint, isExport, isRelease, isPost, parentId, parentName
//   };
// }

// export default function MenuMaster() {
//   const [page, setPage] = useState(0);
//   const [zones, setZones] = useState([]);
//   const [columns, setColumns] = useState<any>([]);
//   const [rowsPerPage, setRowsPerPage] = useState(5);
//   const [rows, setRows] = useState<any>([]);
//   const [records, setRecords] = useState(rows);
//   const [isLoading, setIsLoading] = useState(true);
//   const [menupermisiondata, setMenupermisiondata] = useState<any>();

//   const { i18n, t } = useTranslation();
//   const location = useLocation();



//   const deleteUser = () => {
//     Swal.fire({
//       title: "Are you sure ?",
//       text: "You won't be able to revert this !",
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: "#3085d6",
//       cancelButtonColor: "#d33",
//       confirmButtonText: "Yes, Delete it!",
//     }).then((result: any) => {
//       if (result.isConfirmed) {
//         // console.log("Use clicked ok");
//       } else {
//         // console.log("Use Clicked Cancel");
//       }
//     });
//   };

//   let delete_id = "";
//   const accept = () => {
//     const collectData = {
//       menuId: delete_id,
//     };
//     console.log("collectData " + JSON.stringify(collectData));
//     axios
//       .post(HOST_URL.HOST_URL2 + `Menu/DeleteMenuMaster`, collectData )
//       .then((response) => {
//         if (response.data.isSuccess) {
//           toast.success(response.data.mesg);
//         } else {
//           toast.error(response.data.mesg);
//         }
//         getList();
//       });
//   };
//   const reject = () => {
//     toast.warn("Rejected: You have rejected", { autoClose: 3000 });
//   };

//   const handledeleteClick = (del_id: any) => {
//     delete_id = del_id;
//     confirmDialog({
//       message: "Do you want to delete this record ?",
//       header: "Delete Confirmation",
//       icon: "pi pi-info-circle",
//       acceptClassName: "p=-button-danger",
//       accept,
//       reject,
//     });
//   };

//   useEffect(() => {
//     getList();
//     // var data = JSON.parse(localStorage.getItem("userdata")!);
//     // var menudata = data[0]["userPermission"][0]["parentMenu"];
//     // for (let index = 0; index < menudata.length; index++) {
//     //   var childMenudata = menudata[index]["childMenu"];
//     //   var sas = childMenudata.find((x: any) => x.path == location.pathname);
//     //   if (sas != "undefined") {
//     // setMenupermisiondata(sas);
//     //     break;
//     //   }
//     // }
//   }, []);


//   const routeChangeEdit = (row: any) => {
//     // console.log(row);
//     let path = `/master/MenuMasterEdit`;
//     navigate(path, {
//       state: row,
//     });
//   };

//   /// NEXT PAGE

//   let navigate = useNavigate();
//   const routeChangeAdd = () => {
//     let path = `/master/MenuMasterAdd`;
//     navigate(path);
//   };


//   const getList = async () => {
//     try {
//       const collectData = {
//         menuId: -1
//       };
//       const response = await axios.post(
//         HOST_URL.HOST_URL2 + `Menu/GetMenuMaster`,
//         collectData
//       );
//       const data = response.data.data;
//       const zonesWithIds = data.map((zone: any, index: any) => ({
//         ...zone,
//         serialNo: index + 1,
//         id: zone.menuId,
//       }));
//       setZones(zonesWithIds);
//       setIsLoading(false);

//       if (data.length > 0) {
//         const columns: GridColDef[] = [
//           {
//             field: "actions",
//             headerClassName: "MuiDataGrid-colCell",
//             headerName: t("text.Action"),
//             width: 150,

//             renderCell: (params: any) => {
//               return [
//                 <Stack
//                   spacing={1}
//                   direction="row"
//                   sx={{ alignItems: "center", marginTop: "5px" }}
//                 >
//                   {/* {permissionData?.isEdit ? ( */}
//                   <EditIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "blue",
//                       cursor: "pointer",
//                     }}
//                     className="cursor-pointer"
//                     onClick={() => routeChangeEdit(params.row)}
//                   />
//                   {/* ) : (
//                     ""
//                   )}
//                   {permissionData?.isDel ? ( */}
//                   <DeleteIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "red",
//                       cursor: "pointer",
//                     }}
//                     onClick={() => {
//                       handledeleteClick(params.row.id);
//                     }}
//                   />
//                   {/* ) : (
//                     ""
//                   )} */}
//                   {/* <Switch
//                     checked={Boolean(params.row.isActive)}
//                     style={{
//                       color: params.row.isActive ? "green" : "#FE0000",
//                     }}
//                     onChange={(value: any) =>
//                       handleSwitchChange(value, params.row)
//                     }
//                     inputProps={{
//                       "aria-label": "Toggle Switch",
//                     }}
//                   /> */}
//                 </Stack>,
//               ];
//             },
//           },

//           {
//             field: "serialNo",
//             headerName: t("text.SrNo"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 60
//           },
//           {
//             field: "parentName",
//             headerName: t("text.ParentName"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 120
//           },
//           {
//             field: "menuName",
//             headerName: t("text.MenuName"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 150
//           },
//           {
//             field: "pageUrl",
//             headerName: t("text.PageURL"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 200
//           },
//           {
//             field: "displayNo",
//             headerName: "displayNo",
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 100
//           },
//           {
//             field: "icon",
//             headerName: t("text.Icon"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 120
//           },

//         ];
//         setColumns(columns as any);
//       }

//     } catch (error) {
//       console.error("Error fetching data:", error);
//       // setLoading(false);
//     }
//   };

//   const adjustedColumns = columns.map((column: any) => ({
//     ...column,
//   }));

//   return (
//     <div>
//       <ToastContainer/>
//       <Grid item lg={6} sm={6} xs={12}>
//         <Card
//           style={{
//             width: "100%",
//             height: "50%",
//             backgroundColor: "#E9FDEE",
//             border: ".5px solid green ",
//           }}
//         >
//           <Paper
//             sx={{
//               width: "100%",
//               overflow: "hidden",
//               "& .MuiDataGrid-colCell": {
//                 backgroundColor: `var(--grid-headerBackground)`,
//                 color: `var(--grid-headerColor)`,
//                 fontSize: 17,
//                 fontWeight: 900,
//               },
//             }}
//             style={{ padding: "10px" }}
//           >
//             <ConfirmDialog />
//             <Typography
//               gutterBottom
//               variant="h5"
//               component="div"
//               sx={{ padding: "20px" }}
//               align="left"
//             >
//               {t("text.MenuMaster")}
//             </Typography>
//             <Divider />

//             {/* Search and ADD buttone Start */}
//             <Box height={10} />
//             <Stack direction="row" spacing={2} classes="my-2 mb-2">
//               {/* {menupermisiondata?.isAdd == true ?( */}
//               <Button
//                 onClick={routeChangeAdd}
//                 variant="contained"
//                 endIcon={<AddCircleIcon />}
//               >
//                 {t("text.add")}
//               </Button> {/*):""} */}
//               {/* {menupermisiondata?.isPrint == true ? (
//               <Button variant="contained" endIcon={<PrintIcon />}>
//                  {t("text.print")}
//               </Button>):""}
//               */}

//             </Stack>

//             {/* Search END */}

//             {isLoading ? (
//               <div style={{
//                 display: "flex",
//                 justifyContent: "center",
//                 alignItems: "center",
//               }}>
//                 <CircularProgress />
//               </div>
//             ) : (
//               <DataGrid
//                 rows={zones}
//                 columns={adjustedColumns}
//                 autoHeight
//                 slots={{
//                   toolbar: GridToolbar,
//                 }}
//                 rowSpacingType="border"
//                 pagination={true}
//                 pageSizeOptions={[5, 10, 25, 50, 100].map((size) => ({
//                   value: size,
//                   label: `${size}`,
//                 }))}
//                 initialState={{
//                   pagination: { paginationModel: { pageSize: 5 } },
//                 }}
//                 slotProps={{
//                   toolbar: {
//                     showQuickFilter: true,
//                   },
//                 }}
//               />)}
//           </Paper>
//         </Card>
//       </Grid>
//     </div>
//   );
// }